import { useCallback, useEffect, useMemo } from 'react';
import { LoadingSpinerArea } from '../../../../components';
import { Alert, Container, Dropdown } from 'rsuite';
import {
  CompetitivityCard,
  ConfidenceLevelBadge,
  DownloadButton,
  ElasticityGaugeChart,
  EmptyState,
  FiltersArea,
  MarketShareChart,
  OptimalCompetitivityCard,
  PotentialGainCard,
  ProductReferenceCard,
  ResultsButton,
} from './Components';
import { PageBlocker } from '../InfoPanel/Components/PageBlocker';
import { PAGE_BLOCKER } from './constants';
import styles from './ElasticidadeCruzada.module.scss';
import { useElasticidadeCruzadaStore } from './ElasticidadeCruzada.store';
import { useElasticityData } from './hooks';
import { downloadElasticityCurves, type DownloadFormat } from './services';
import { User } from '../../../../@types';
import { ModalSpeakWithSpecialist } from '../../../../components/ModalSpeakWithSpecialist';
import { INFOPANEL_CTA_LINK } from '../../../../utils/constants';
import { trackEvent } from '../../../../utils/MatomoConfig';
import { sendLead } from '../InfoPanel/utils';

const VIEW_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  EMPTY: 'empty',
} as const;

type ViewStatus = (typeof VIEW_STATUS)[keyof typeof VIEW_STATUS];

export type ElasticidadeCruzadaProps = {
  usuario: User;
};

export const ElasticidadeCruzada = ({ usuario }: ElasticidadeCruzadaProps) => {
  const {
    elasticityResponse,
    marketShareResponse,
    isEmptyResponse,
    reset,
    produtoReferencia,
    produtosCompetidores,
    tipoConcorrente,
    tipoLocalizacao,
    localizacao,
    segmentos,
    subsegmentos,
    showModalSpeakWithSpecialistElasticidadeCruzada,
    setState,
  } = useElasticidadeCruzadaStore();

  const { fetchData, isLoading } = useElasticityData();

  useEffect(() => reset, [reset]);

  const hasFetched = useMemo(() => {
    return elasticityResponse !== null || marketShareResponse !== null || isEmptyResponse === true;
  }, [elasticityResponse, marketShareResponse, isEmptyResponse]);

  const hasResults = useMemo(() => {
    return hasFetched && !isLoading && !isEmptyResponse;
  }, [hasFetched, isLoading, isEmptyResponse]);

  const status: ViewStatus = useMemo(() => {
    if (isLoading) return VIEW_STATUS.LOADING;
    if (isEmptyResponse) return VIEW_STATUS.EMPTY;
    if (hasResults) return VIEW_STATUS.SUCCESS;
    return VIEW_STATUS.IDLE;
  }, [isLoading, isEmptyResponse, hasResults]);

  const confidence = marketShareResponse?.competitividade;

  const handleSearch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      if (!hasFetched) {
        return;
      }

      const productBarcode = String(produtoReferencia.value?.[0] || '');

      if (!productBarcode) {
        Alert.warning('Selecione um produto de referência para realizar o download.', 5000);
        return;
      }

      try {
        await downloadElasticityCurves(format, {
          productBarcode,
          competitorBarcode: tipoConcorrente === 'PRODUTO_ESPECIFICO' ? String(produtosCompetidores.value?.[0] || '') || undefined : undefined,
          segmentation: (segmentos.value?.[0] as string) || undefined,
          subSegmentation: (subsegmentos.value?.[0] as string) || undefined,
          region: tipoLocalizacao === 'REGIAO' ? (localizacao.value?.[0] as string) || undefined : undefined,
        });
      } catch (error) {
        console.error('Erro ao realizar download da curva:', error);
        Alert.error('Erro ao realizar download da curva. Tente novamente.', 5000);
      }
    },
    [hasFetched, localizacao.value, produtoReferencia.value, produtosCompetidores.value, segmentos.value, subsegmentos.value, tipoConcorrente, tipoLocalizacao],
  );

  const HAS_NO_PERMISSION = !usuario?.tipoAdicionalContratos?.PAN?.includes('ELASTICIDADE_CRUZADA');

  const handleSendCTA = () => {
    const identificador = 'upsell-elasticidade-cruzada';
    sendLead(usuario, identificador);
    setState('showModalSpeakWithSpecialistElasticidadeCruzada', false);
    trackEvent('Upsell/Crossell', 'upsell-free-users-elasticidade-cruzada');
    window.open(INFOPANEL_CTA_LINK);
  };

  return (
    <Container className={`main-container ${styles.container}`}>
      <LoadingSpinerArea area="elasticidade-cruzada" />
      <Header />

      <div className={styles.innerContainer}>
        {HAS_NO_PERMISSION && (
          <PageBlocker
            id="elasticidade-cruzada-blocker-btn"
            className={styles.pageBlockerElasticidadeCruzada}
            title={PAGE_BLOCKER.title}
            subtitle={PAGE_BLOCKER.subtitle}
            buttonText="quero o teste grátis"
            onClick={() => setState('showModalSpeakWithSpecialistElasticidadeCruzada', true)}
          />
        )}
        <div className={styles.filtersSection}>
          <FiltersArea />
        </div>

        <ActionsRow onSearch={handleSearch} hasFetched={hasFetched} hasResults={hasResults} confidence={confidence} onDownload={handleDownload} />

        {status === VIEW_STATUS.SUCCESS && <Results />}

        {status === VIEW_STATUS.EMPTY && <EmptyState />}
        <ModalSpeakWithSpecialist
          show={showModalSpeakWithSpecialistElasticidadeCruzada}
          title={PAGE_BLOCKER.modal.title}
          subtitle={PAGE_BLOCKER.modal.subtitle}
          onCancel={() => setState('showModalSpeakWithSpecialistElasticidadeCruzada', false)}
          onClick={handleSendCTA}
        />
      </div>
    </Container>
  );
};

const Header = () => (
  <div className={styles.header}>
    <h1 className={styles.title}>Elasticidade Cruzada</h1>
    <p className={styles.subtitle}>Calcule e acompanhe a elasticidade cruzada do seu produto versus concorrentes</p>
  </div>
);

type ActionsRowProps = {
  onSearch: () => void;
  hasFetched: boolean;
  hasResults: boolean;
  confidence?: { confianca: string; badgeColor: string };
  onDownload: (format: DownloadFormat) => void;
};

const ActionsRow = ({ onSearch, onDownload, hasFetched, hasResults, confidence }: ActionsRowProps) => (
  <div className={styles.actionsRow}>
    <div className={styles.resultsButtonWrapper}>
      <ResultsButton onClick={onSearch} hasDataRendered={hasFetched} />
    </div>

    {!!confidence?.confianca && hasFetched && <ConfidenceLevelBadge nivel={confidence.confianca} cor={confidence.badgeColor} />}

    <div className={styles.rightActions}>
      <Dropdown id="elasticidade-cruzada-download" placement="bottomEnd" renderTitle={() => <DownloadButton disabled={!hasFetched} />}>
        <Dropdown.Item disabled={!hasFetched} onClick={() => onDownload('CSV')} id="elasticidade-cruzada-download-csv">
          Download em CSV
        </Dropdown.Item>
        <Dropdown.Item disabled={!hasFetched} onClick={() => onDownload('XLSX')} id="elasticidade-cruzada-download-xlsx">
          Download em XLSX
        </Dropdown.Item>
      </Dropdown>
      {/* TODO: reativar quando a API estiver pronta */}
      {/* <MonitorCompetitiveness disabled={!hasFetched} onClick={onMonitor} /> */}
    </div>
  </div>
);

const Results = () => (
  <div className={styles.resultsContainer}>
    <div className={styles.chartsRow}>
      <div className={styles.leftColumn}>
        <ProductReferenceCard />
        <ElasticityGaugeChart />
        <CompetitivityCard />

        {/* <PotentialGainCard /> */}
        {/* <div className={styles.summaryCardsRow}>
          <OptimalCompetitivityCard />
          <CompetitivityCard />
        </div> */}
      </div>

      <div className={styles.rightColumn}>
        <MarketShareChart />
      </div>
    </div>
  </div>
);
